import { Field, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { StyledContainer, StyledSuggestions } from "./address-autocomplete.styles";

const IDEAL_POSTCODES_API_KEY = process.env.GATSBY_IDEAL_POSTCODES_API_KEY as string;

export const AddressAutocomplete = ({ id, name, placeholder, postcodeField, cityField }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<{ id: string; suggestion: string }[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!inputValue || inputValue.length < 3) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses?query=${encodeURIComponent(inputValue)}&api_key=${IDEAL_POSTCODES_API_KEY}`
        );

        if (!response.ok) {
          throw new Error(response.statusText);
        }

        const data = await response.json();
        // Suggestions are in result.hits[], each with .id and .suggestion
        setSuggestions(data.result.hits);
      } catch (err) {
        console.error("Autocomplete fetch failed:", err);
        setSuggestions([]);
      }
    };

    if(id && inputValue){
      setFieldValue(id, inputValue);
    }

    fetchSuggestions();
  }, [inputValue]);

  const handleSelection = (addressId: string) => async () => {
    setIsOpen(false);

    if (id) {
      try {
        // Resolve the suggestion to a full GBR address
        const response = await fetch(
          `https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses/${encodeURIComponent(addressId)}/gbr?api_key=${IDEAL_POSTCODES_API_KEY}`
        );

        if (!response.ok) {
          throw new Error(response.statusText);
        }

        const data = await response.json();
        const address = data.result;

        const streetAddress = [address.line_1, address.line_2, address.line_3].filter(Boolean).join(", ");
        const postcode = address.postcode;
        const city = address.post_town;

        setInputValue(streetAddress);
        setFieldValue(id, streetAddress);

        if (postcodeField) {
          setFieldValue(postcodeField, postcode);
        }

        if (cityField) {
          setFieldValue(cityField, city);
        }
      } catch (err) {
        console.error("Address resolve failed:", err);
      }
    }
  };

  return (
    <StyledContainer>
      <Field
        id={id}
        type="text"
        as="input"
        autoComplete="off"
        name={name}
        placeholder={placeholder}
        value={inputValue}
        onBlur={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => setInputValue(e.target.value)}
      />
      <StyledSuggestions $open={isOpen && suggestions.length > 0}>
        {suggestions.map((suggestion) => (
          <li key={suggestion.id} onMouseDown={handleSelection(suggestion.id)}>
            {suggestion.suggestion}
          </li>
        ))}
      </StyledSuggestions>
    </StyledContainer>
  );
};